import { I18n } from "react-redux-i18n";
import { Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle } from '@common/components/modal';

import { getTestAttributes } from "@common/utils/tests";
import { Flex } from "@common/components/Flex";
import { Button } from "@common/components/Button";
import { Loading } from "@common/components/loading";
import { EmailTemplate } from "@common/components/email-template";
import { EmailAddress } from "./components/email-address";
import { isEmail } from '@common/utils/email';
import { PedigreeFolder } from "@common/components/pedigree-folder";
import { Questionnaires } from "@common/components/questionnaires";

import "./new-invite.styles.scss";

export const NEW_INVITE_STEPS = {
  questionnaires: "questionnaires",
  emailTemplate: "emailTemplate",
  emailAddress: "emailAddress",
  pedigreeFolder: "pedigreeFolder",
};
export const NEW_INVITE_QUESTIONNAIRE_TYPE = {
  individual: 'INDIVIDUAL',
  family: 'FAMILY'
};

export const NewInvite = (props) => {
  const { 
    onClose,
    onSubmit,
    showErrorMessage,
    showInfoMessage,
    individualId,
    inviteId,
    getSteps,
    getQuestionnaires,
    getIndividualEmail,
    saveIndividualEmail,
    getQuestionnaireForInvite,
    checkPedigreeNameExists,
    createPedigreeAndMoveToFolder,
    getPedigreeFolders,
    getIndividualFamilyData,
    getFamilyIdMembersCount,
    getStepsConfig,
    defaultQuestionnaire,
    defaultEmailTemplate,
    defaultEmailSubject,
    title,
   } = props;
  const [currentStep, setCurrentStep] = React.useState();
  const [submitting, setSubmitting] = React.useState(false);
  const [selectedQuestionnaire, setSelectedQuestionnaire] = React.useState(defaultQuestionnaire);
  const [emailSubject, setEmailSubject] = React.useState(defaultEmailSubject ?? 'Family History Questionnaire');
  const [emailTemplate, setEmailTemplate] = React.useState(defaultEmailTemplate ?? '');
  const [loadingEmailState, setLoadingEmailState] = React.useState(false);
  const [hasSavedEmail, setSavedEmailState] = React.useState(false);
  const [emailAddress, setEmailAddress] = React.useState('');
  const [individualFamilyData, setIndividualFamilyData] = React.useState();
  const [initializingComponent, setInitializingComponentState] = React.useState(true);
  const [individualFamilyMembersCount, setIndividualFamilyMembersCount] = React.useState();
  const [dataToMoveIndividual, setDataToMoveIndividual] = React.useState({ name: '', folderId: null });

  const handleSelectedQuestionnaireChange = React.useCallback((item) => {
    setSelectedQuestionnaire(item);
    setEmailTemplate(item.emailTemplate.body);
    setEmailSubject(item.emailTemplate.subject);
  }, []);

  const stepsConfig = getStepsConfig ? getStepsConfig() : undefined;

  const stepsFromOuterProps = getSteps 
    ? getSteps({
      questionnaire: selectedQuestionnaire,
      hasSavedEmail,
      individualFamilyData,
    }) 
    : undefined;

  const getEmailState = (questionnaire) => {
    if (!getIndividualEmail) {
      return;
    }

    setLoadingEmailState(true);
    return getIndividualEmail(questionnaire)
      .then((email) => {
        if (email?.length && isEmail(email)) {
          setSavedEmailState(true);
        } else {
          const notValidEmail = email || '';

          setEmailAddress(notValidEmail);
        }

        setLoadingEmailState(false);
      })
      .catch((err) => {
        console.error(err);
        setLoadingEmailState(false);
      });
  };

  React.useEffect(() => {
    const initialize = async () => {
      try {
        if (inviteId) {
          await getQuestionnaireForInvite(inviteId).then(async (data) => {
            setSelectedQuestionnaire(data);
            await getEmailState(data);
            setEmailTemplate(data.reminderEmailTemplate?.body);
            setEmailSubject(data.reminderEmailTemplate?.subject);
          });
        }  
        
        if (individualId) {
          await getIndividualFamilyData(individualId).then((data) => {
            setIndividualFamilyData(data);
    
            return getFamilyIdMembersCount(data.familyId).then((count) => {
              setIndividualFamilyMembersCount(count);
            });
          });
        }
      } catch (err) {
        console.error(err);
        showErrorMessage(err?.entity || I18n.t("Failed to get data"));
      }

      if (defaultQuestionnaire) {
        await getEmailState(defaultQuestionnaire);
      }
  
      setInitializingComponentState(false);
    };

    initialize();
  }, []);

  React.useEffect(() => {
    if (initializingComponent) {
      return;
    }

    if (stepsFromOuterProps) {
      const firstAllowedStep = stepsFromOuterProps.find((x) => x.condition)?.step;

      setCurrentStep(firstAllowedStep === NEW_INVITE_STEPS.questionnaires
        ? NEW_INVITE_STEPS.questionnaires
        : getNextStepFromQuestionnaires()
      );
    } else {
      setCurrentStep(NEW_INVITE_STEPS.questionnaires);
    }
  }, [initializingComponent]);

  React.useEffect(() => {
    if (selectedQuestionnaire) {
      getEmailState(selectedQuestionnaire);
    }
  }, [selectedQuestionnaire]);

  const saveEmail = async () => {
    try {
      setSubmitting(true);

      await saveIndividualEmail({
        questionnaireId: selectedQuestionnaire.questionnaireId,
        globalId: props.individualId,
        wdcFolderId: selectedQuestionnaire.wdcFolderId,
        email: emailAddress,
        fhq4: selectedQuestionnaire.fhq4,
      });
      setSavedEmailState(true);
      setSubmitting(false);
      
      return true;
    } catch (err) {
      setSubmitting(false);
      showErrorMessage(err?.entity || I18n.t("Operation failed"));
      console.error(err);

      return false;
    }
  };
  
  const validateSelectedQuestionnaire = React.useCallback(() => {
    if (Boolean(selectedQuestionnaire?.datasheetFormatName?.length) || selectedQuestionnaire.fhq4) {
      return true;
    }

    showInfoMessage(I18n.t("Selected questionnaire doesn't have Datasheet Format"));

    return false;
  }, [showInfoMessage, selectedQuestionnaire]);

  const createPedigree = async () => {
    try {
      const exists = await checkPedigreeNameExists(dataToMoveIndividual.folderId, dataToMoveIndividual.name);

      if (exists) {
        showInfoMessage(I18n.t("The Pedigree Name you entered is already used in this database, please choose another name"));

        return false;
      }
    } catch (err) {
      console.error(err);
      showInfoMessage(err.entity || I18n.t("The Pedigree Name you entered is already used in this database, please choose another name"));

      return false;
    }
    
    try {
      await createPedigreeAndMoveToFolder({
        pedigreeName: dataToMoveIndividual.name,
        pedigreeFolderId: dataToMoveIndividual.folderId,
        familyId: individualFamilyData.familyId,
      });

      const data = await getIndividualFamilyData(individualId);
      setIndividualFamilyData(data);

      const count = getFamilyIdMembersCount(data.familyId);
      setIndividualFamilyMembersCount(count);

      return true;
    } catch (err) {
      console.error(err);
      showErrorMessage(err.entity || I18n.t("Failed to create pedigree"));

      return false;
    }
  };

  const getAvailableSteps = (defaultSteps) => {
    if (stepsFromOuterProps) {
      defaultSteps = defaultSteps.reduce((acc, defaultStepConfig) => {
        const outerStepConfig = stepsFromOuterProps.find((outerStepConfig) => {
          return (outerStepConfig.condition ?? true) && outerStepConfig.step === defaultStepConfig.step;
        });

        if (outerStepConfig) {
          acc.push({
            step: defaultStepConfig.step,
            condition: outerStepConfig.condition ?? defaultStepConfig.condition,
          });
        }

        return acc;
      }, []);
    }

    return defaultSteps;
  };

  const getNextStepFromQuestionnaires = (quest = selectedQuestionnaire) => {
    const config = getAvailableSteps([
      {
        condition: quest?.fhq4 && hasSavedEmail,
        step: NEW_INVITE_STEPS.emailTemplate,
      },
      {
        condition: quest?.fhq4 && !hasSavedEmail,
        step: NEW_INVITE_STEPS.emailAddress,
      },
      {
        condition: quest?.questionnaireType === NEW_INVITE_QUESTIONNAIRE_TYPE.family && individualFamilyData && !individualFamilyData.familyName,
        step: NEW_INVITE_STEPS.pedigreeFolder,
      },
      {
        condition: quest?.questionnaireType === NEW_INVITE_QUESTIONNAIRE_TYPE.family && individualFamilyData && individualFamilyData.familyName && !hasSavedEmail,
        step: NEW_INVITE_STEPS.emailAddress,
      },
      {
        condition: quest?.questionnaireType === NEW_INVITE_QUESTIONNAIRE_TYPE.family && individualFamilyData && individualFamilyData.familyName && hasSavedEmail,
        step: NEW_INVITE_STEPS.emailTemplate,
      },
      {
        condition: quest?.questionnaireType === NEW_INVITE_QUESTIONNAIRE_TYPE.individual && !hasSavedEmail,
        step: NEW_INVITE_STEPS.emailAddress,
      },
      {
        condition: quest?.questionnaireType === NEW_INVITE_QUESTIONNAIRE_TYPE.individual && hasSavedEmail,
        step: NEW_INVITE_STEPS.emailTemplate,
      },
    ]);

    return config.find((x) => x.condition)?.step;
  };

  const getNextStepFromPedigreeFolder = () => {
    const config = getAvailableSteps([
      {
        condition: selectedQuestionnaire?.questionnaireType === NEW_INVITE_QUESTIONNAIRE_TYPE.family && !hasSavedEmail,
        step: NEW_INVITE_STEPS.emailAddress,
      },
      {
        condition: selectedQuestionnaire?.questionnaireType === NEW_INVITE_QUESTIONNAIRE_TYPE.family && hasSavedEmail,
        step: NEW_INVITE_STEPS.emailTemplate,
      },
    ]);

    return config.find((x) => x.condition)?.step;
  };

  const confirmStepIfAccessible = (step) => {
    if (stepsFromOuterProps) {
      return stepsFromOuterProps.some((x) => x.step === step) && step;
    }

    return step;
  };

  const steps = {
    [NEW_INVITE_STEPS.questionnaires]: {
      buttonProps: {
        disabled: selectedQuestionnaire === undefined || (selectedQuestionnaire && loadingEmailState),
      },
      component: (
        <Questionnaires
          getData={() => getQuestionnaires({
            includeFamilyQuestionnaires: individualFamilyMembersCount > 1 ? false : true,
            includeFhq4Questionnaires: true,
            includeHiddenQuestionnaires: false
          })} 
          title={I18n.t("Select a Questionnaire you want to invite patients to")}
          selectedQuestionnaire={selectedQuestionnaire} 
          onSelectedQuestionnaireChange={handleSelectedQuestionnaireChange}
        />
      ),
      executeBeforeGoNextStep: validateSelectedQuestionnaire,
      nextStep: getNextStepFromQuestionnaires(),
    },
    [NEW_INVITE_STEPS.pedigreeFolder]: {
      buttonProps: {
        disabled: !dataToMoveIndividual.name || !dataToMoveIndividual.folderId
      },
      component: (
        <PedigreeFolder
          getData={getPedigreeFolders} 
          name={dataToMoveIndividual.name} 
          folderId={dataToMoveIndividual.folderId}
          onChange={setDataToMoveIndividual}
        />
      ),
      executeBeforeGoNextStep: createPedigree,
      prevStep: confirmStepIfAccessible(NEW_INVITE_STEPS.questionnaires),
      nextStep: getNextStepFromPedigreeFolder(),
    },
    [NEW_INVITE_STEPS.emailAddress]: {
      buttonProps: {
        disabled: !emailAddress || !isEmail(emailAddress),
      },
      component: (
        <EmailAddress 
          defaultValue={emailAddress}
          onChange={setEmailAddress}
        />
      ),
      executeBeforeGoNextStep: saveEmail,
      prevStep: confirmStepIfAccessible(NEW_INVITE_STEPS.questionnaires),
      nextStep: NEW_INVITE_STEPS.emailTemplate,
    },
    [NEW_INVITE_STEPS.emailTemplate]: {
      component: (
        <EmailTemplate 
          subject={emailSubject}
          onSubjectChange={setEmailSubject}
          template={emailTemplate}
          onTemplateChange={setEmailTemplate}
        />
      ),
      prevStep: confirmStepIfAccessible(NEW_INVITE_STEPS.questionnaires),
      submitBtnText: stepsConfig ? stepsConfig[NEW_INVITE_STEPS.emailTemplate]?.submitBtnText : I18n.t('Send an Invite')
    },
  };
  const currentStepConfig = steps[currentStep];

  const handleGoToPrevStep = React.useCallback((e) => {
    setEmailAddress('');
    setDataToMoveIndividual({ email: '', folderId: null });
    setCurrentStep(currentStepConfig.prevStep);
  }, [currentStepConfig?.prevStep]);

  const handleGoToNextStep = async (e) => {
    if (currentStepConfig.executeBeforeGoNextStep) {
      const isSuccess = await currentStepConfig.executeBeforeGoNextStep();

      if (!isSuccess) {
        return;
      }
    }

    setCurrentStep(currentStepConfig?.nextStep);
  };

  const handleSubmit = async () => {
    setSubmitting(true);

    try {
      await onSubmit({
        questionnaireId: selectedQuestionnaire.questionnaireId,
        wdcfolderId: selectedQuestionnaire.wdcFolderId,
        globalId: props.individualId,
        emailTemplate: {
          ...selectedQuestionnaire.emailTemplate,
          body: emailTemplate,
          subject: emailSubject,
        },
        fhq4: selectedQuestionnaire.fhq4,
        reminder: props.inviteId > 0,
        inviteId: props.inviteId,
      });
    } catch (err) {
      setSubmitting(false);
      showErrorMessage(err?.entity || I18n.t("Operation failed"), 'medium');
      console.error(err);
    }
  };

  return (
    <Modal
      show
      animation={false}
      bsSize="medium"
      backdrop="static"
      bsClass="bootstrap modal"
      className="IndividualsNewInvite"
      onEscapeKeyDown={onClose}
      onHide={onClose}
    >
      <ModalHeader closeButton onHide={onClose}>
        <ModalTitle
          {...getTestAttributes({ id: 'btn-bw-parent-close' })}
        >
          {title || I18n.t("Invite Patient to the Questionnaire")}
        </ModalTitle>
      </ModalHeader>

      <ModalBody>
        {initializingComponent || typeof currentStepConfig == 'undefined'
          ? <Flex className="IndividualsNewInvite__loading" justify="center" alignItems="center"><Loading size={30} /></Flex>
          : currentStepConfig.component
        }
      </ModalBody>
      <ModalFooter>
        <Button
          disabled={submitting}
          {...getTestAttributes({ id: 'btn-bw-parent-cancel' })}
          onClick={onClose}
        >
          {I18n.t("Cancel")}
        </Button>
        <Flex>
          {currentStepConfig?.prevStep && (
            <Button
              className="IndividualsNewInvite__previous-btn"
              disabled={submitting}
              {...getTestAttributes({ id: 'btn-bw-parent-previous' })}
              onClick={handleGoToPrevStep}
            >
              {I18n.t("Previous")}
            </Button>
          )}
          <Button
            disabled={submitting}
            bsStyle="primary"
            {...getTestAttributes({ id: 'btn-bw-parent-next' })}
            {...currentStepConfig?.buttonProps}
            onClick={currentStepConfig?.nextStep ? handleGoToNextStep : handleSubmit}
          >
            {submitting ? I18n.t("Loading") : I18n.t(currentStepConfig?.submitBtnText || "Next")}
          </Button>
        </Flex>
      </ModalFooter>
    </Modal>
  );
};
